import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  ClipboardList,
  Eye,
  Pill,
  RefreshCw,
  Search,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { ClinicalDocKind } from '@datazo/contracts';
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/shared/store/page';
import { cn } from '@/shared/lib/cn';
import { useClinicalDocs, type ClinicalDocsFilters } from './api';

interface KindConfig {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  signerHeader: string;
  detailPath: (procedimientoId: string) => string;
}

const CONFIG: Record<ClinicalDocKind, KindConfig> = {
  consent: {
    title: 'Consentimientos firmados',
    subtitle: 'Documentos clínicos · Consentimiento informado',
    icon: ClipboardList,
    signerHeader: 'Médico',
    detailPath: (id) => `/consentimiento/${id}`,
  },
  anesthesia: {
    title: 'Registros de anestesia firmados',
    subtitle: 'Documentos clínicos · Registro de anestesia',
    icon: Activity,
    signerHeader: 'Anestesiólogo',
    detailPath: (id) => `/anestesia/${id}`,
  },
  expense: {
    title: 'Hojas de gasto firmadas',
    subtitle: 'Documentos clínicos · Hoja de gasto',
    icon: Pill,
    signerHeader: 'Enfermera',
    detailPath: (id) => `/gasto/${id}`,
  },
};

const PAGE_SIZE = 20;

function formatSignedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ClinicalDocsListPage({ kind }: { kind: ClinicalDocKind }): JSX.Element {
  const config = CONFIG[kind];
  usePageTitle(config.title, config.subtitle);
  const navigate = useNavigate();

  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [tipo, setTipo] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [page, setPage] = useState(1);

  // Debounce del buscador de texto.
  useEffect(() => {
    const t = window.setTimeout(() => setQ(qInput), 300);
    return () => window.clearTimeout(t);
  }, [qInput]);

  // Cualquier cambio de filtro vuelve a la primera página.
  useEffect(() => {
    setPage(1);
  }, [q, tipo, desde, hasta]);

  const filters = useMemo<ClinicalDocsFilters>(
    () => ({ q, tipo, desde, hasta, page, pageSize: PAGE_SIZE }),
    [q, tipo, desde, hasta, page],
  );

  const query = useClinicalDocs(kind, filters);
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const hasFilters = q !== '' || tipo !== '' || desde !== '' || hasta !== '';
  const clearFilters = (): void => {
    setQInput('');
    setQ('');
    setTipo('');
    setDesde('');
    setHasta('');
  };

  const Icon = config.icon;

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader>
          <div>
            <CardTitle className="flex items-center gap-2">
              <Icon className="w-4 h-4 text-primary" /> {config.title}
            </CardTitle>
            <CardDescription>
              {query.isLoading
                ? 'Cargando…'
                : `${total} documento${total === 1 ? '' : 's'} firmado${total === 1 ? '' : 's'}`}
            </CardDescription>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={cn('w-4 h-4', query.isFetching && 'animate-spin')} /> Actualizar
          </Button>
        </CardHeader>

        {/* Filtros */}
        <div className="px-6 py-4 border-t border-border grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="q">Buscar</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <Input
                id="q"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="Paciente, documento o N.º de episodio…"
                className="pl-9"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tipo">Tipo de procedimiento</Label>
            <Input
              id="tipo"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              placeholder="Ej. colonoscopia"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="desde">Firmado desde</Label>
            <Input id="desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hasta">Firmado hasta</Label>
            <Input id="hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </div>
          {hasFilters && (
            <div className="xl:col-span-4">
              <Button size="sm" variant="ghost" onClick={clearFilters}>
                <X className="w-4 h-4" /> Limpiar filtros
              </Button>
            </div>
          )}
        </div>

        {/* Tabla */}
        {query.isError ? (
          <div className="p-8 text-center text-sm text-text-muted border-t border-border">
            No se pudieron cargar los documentos.{' '}
            <button
              onClick={() => void query.refetch()}
              className="text-primary font-semibold underline"
            >
              Reintentar
            </button>
          </div>
        ) : query.isLoading ? (
          <div className="p-6 space-y-3 border-t border-border" aria-busy="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-10 rounded bg-surface-alt animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center flex flex-col items-center gap-2 text-text-muted border-t border-border">
            <Icon className="w-8 h-8 opacity-50" />
            <p className="text-sm">
              {hasFilters
                ? 'Ningún documento coincide con los filtros.'
                : 'Aún no hay documentos firmados de este tipo.'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Episodio</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Paciente</TableHead>
                <TableHead className="w-[130px]">Documento</TableHead>
                <TableHead>{config.signerHeader}</TableHead>
                <TableHead className="w-[160px]">Firmado el</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((doc) => (
                <TableRow
                  key={doc.id}
                  className="cursor-pointer hover:bg-surface-alt"
                  onClick={() => navigate(config.detailPath(doc.procedimientoId))}
                >
                  <TableCell>
                    <code className="text-[11px] font-mono text-text-muted">
                      {doc.procedimientoId}
                    </code>
                  </TableCell>
                  <TableCell className="text-sm">{doc.procedureTipo}</TableCell>
                  <TableCell className="text-sm font-medium">{doc.pacienteNombre}</TableCell>
                  <TableCell className="text-xs text-text-muted">{doc.pacienteDocumento}</TableCell>
                  <TableCell className="text-sm">{doc.firmantePor}</TableCell>
                  <TableCell className="text-xs text-text-muted">
                    {formatSignedAt(doc.firmadoEn)}
                  </TableCell>
                  <TableCell>
                    <Eye className="w-4 h-4 text-text-muted" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {totalPages > 1 && (
          <CardFooter className="flex items-center justify-between">
            <span className="text-xs text-text-muted">
              Página {page} de {totalPages} · {total} en total
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={page <= 1 || query.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={page >= totalPages || query.isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Siguiente
              </Button>
            </div>
          </CardFooter>
        )}
      </Card>

      <p className="text-xs text-text-muted px-1">
        Haz clic en un documento para abrir su detalle firmado.
      </p>
    </div>
  );
}
